import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import { MemberPosition, MemberStatus } from "@satyrsmc/shared/lib/enums";
import { trpc } from "@/trpc";
import { Button } from "@satyrsmc/shared/components";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface SendPostEventEmailDialogProps {
  eventId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Sends the canned post-event thank-you email (gallery + feedback links) to
 * the event's confirmed attendees. The content is fixed server-side — the
 * admin only picks who it's sent from, so the explicit Send here is the
 * confirmation step.
 */
export function SendPostEventEmailDialog({
  eventId,
  open,
  onOpenChange,
}: SendPostEventEmailDialogProps) {
  const [fromMemberId, setFromMemberId] = useState<string | undefined>(undefined);

  const { data: members } = trpc.admin.members.list.useQuery(undefined, { enabled: open });

  const activeMembers = useMemo(() => {
    return (members ?? [])
      .filter((m) => m.status === MemberStatus.Active && m.email)
      .map((m) => ({ id: m.id, name: m.name, position: m.position ?? null }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [members]);

  // Default the "from" select to the reigning Correspondence Secretary, if one exists.
  const correspondenceSecretaryId = useMemo(
    () => activeMembers.find((m) => m.position === MemberPosition.CorrespondenceSecretary)?.id,
    [activeMembers],
  );
  const effectiveFromMemberId = fromMemberId ?? correspondenceSecretaryId;

  const sendPostEventEmail = trpc.admin.events.sendPostEventEmail.useMutation({
    onSuccess: ({ sent, skipped }) => {
      const base = `Post-event email sent to ${sent} attendee${sent === 1 ? "" : "s"}`;
      toast.success(skipped > 0 ? `${base} · ${skipped} skipped (no email)` : base);
      handleOpenChange(false);
    },
    onError: (err) => toast.error(err.message),
  });

  function handleOpenChange(next: boolean) {
    if (!next) setFromMemberId(undefined);
    onOpenChange(next);
  }

  const canSend = !sendPostEventEmail.isPending && !!effectiveFromMemberId;

  function handleSend() {
    if (!effectiveFromMemberId) return;
    sendPostEventEmail.mutate({
      event_id: eventId,
      from_member_id: effectiveFromMemberId,
    });
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Send Post-Event Email</DialogTitle>
          <DialogDescription>
            Thanks confirmed attendees and links them to the event gallery and feedback form — sent
            to confirmed attendees only.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-1.5">
          <p className="text-sm font-medium">From</p>
          <Select value={effectiveFromMemberId} onValueChange={setFromMemberId}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Choose a sender…" />
            </SelectTrigger>
            <SelectContent>
              {activeMembers.map((m) => (
                <SelectItem key={m.id} value={m.id}>
                  {m.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSend} disabled={!canSend} className="gap-1.5">
            {sendPostEventEmail.isPending ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Send className="size-4" />
            )}
            {sendPostEventEmail.isPending ? "Sending…" : "Send"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
